// The team: people, roles, the kind of calls each makes, invitations and phones (TEAM-01..10). Owners and admins
// manage; managers look.
import { type FormEvent, useState } from "react";
import { asCallType, CALL_TYPES, callTypeLabel, MAX_NOTE } from "../lib/aiInstructions";
import { fmtDateTime, fmtPhone, isAdminRole, isManagerRole, roleLabel, ROLES, syncLine } from "../lib/format";
import { phoneReadiness } from "../lib/readiness";
import type { Device, Member, QaRole, Role } from "../lib/types";
import { useMember } from "../app/session";
import { Card, ConfirmButton, Empty, ErrorNote, Field, Loading, Modal, Page, useAction, useLoad } from "../app/ui";

export function Team() {
  const { api, member, tz } = useMember();
  const { data, error, loading, reload } = useLoad(() => api.team(), [api]);
  const { busy, run } = useAction();
  const [noteFor, setNoteFor] = useState<Member | null>(null);
  const admin = isAdminRole(member.role);
  const owner = member.role === "owner";

  const act = async (fn: () => Promise<void>, done: string) => {
    if (await run(fn, done)) await reload();
  };

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;

  const active = data.members.filter((m) => m.status === "active");
  const removed = data.members.filter((m) => m.status === "removed");
  const deviceOf = (id: string) => data.devices.find((d) => d.member_id === id && !d.revoked_at);
  const plan = member.features;
  // Without Coach or Autopilot the AI's settings are locked (D-181).
  const aiOn = Boolean(plan?.coach || plan?.autopilot);
  const names = new Map(data.members.map((m) => [m.id, m.display_name]));

  return (
    <Page title="Team" sub={`${active.length} people`}>
      <ErrorNote error={error} />
      <div className="stack">
        <Card title="People" flush>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th title={aiOn ? "The AI judges cold and conversion calls differently" : undefined}>Calls</th>
                  <th>Contact</th>
                  <th>Phone app</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((m) => {
                  const device = deviceOf(m.id);
                  const canEdit = admin && m.id !== member.member_id &&
                    (owner || (m.role !== "owner" && m.role !== "admin"));
                  return (
                    <tr key={m.id}>
                      <td className="primary">
                        {m.display_name}
                        {m.id === member.member_id && <span className="faint">(you)</span>}
                      </td>
                      <td>
                        {canEdit
                          ? (
                            <select
                              value={m.role}
                              disabled={busy}
                              aria-label={`Role of ${m.display_name}`}
                              onChange={(e) =>
                                void act(
                                  () => api.setRole(m.id, e.target.value as Role),
                                  "Role changed",
                                )}
                            >
                              {ROLES.filter((r) => owner || (r !== "owner" && r !== "admin")).map((r) => (
                                <option key={r} value={r}>
                                  {roleLabel[r]}
                                </option>
                              ))}
                            </select>
                          )
                          : roleLabel[m.role]}
                        {aiOn && admin
                          ? (
                            // Call review (D-112): who checks the AI's reading of the calls.
                            <select
                              value={m.qa_role ?? ""}
                              disabled={busy}
                              aria-label={`Call review by ${m.display_name}`}
                              onChange={(e) =>
                                void act(
                                  () => api.qaSetRole(m.id, (e.target.value || null) as QaRole | null),
                                  e.target.value ? "Reviews calls now" : "No longer reviews calls",
                                )}
                            >
                              <option value="">Doesn't review calls</option>
                              <option value="tester">Reviews every call (tester)</option>
                              <option value="senior">Reviews flagged calls (senior)</option>
                            </select>
                          )
                          : aiOn && m.qa_role && (
                            <div className="faint">{m.qa_role === "senior" ? "Senior reviewer" : "Reviews calls"}</div>
                          )}
                      </td>
                      <td>
                        {!aiOn ? <div className="faint">AI is not enabled for your organization</div> : admin
                          ? (
                            <select
                              value={m.call_type ?? ""}
                              disabled={busy}
                              aria-label={`Calls ${m.display_name} makes`}
                              onChange={(e) =>
                                void act(
                                  () => api.setCallProfile(m.id, { call_type: asCallType(e.target.value) }),
                                  "Call type saved",
                                )}
                            >
                              <option value="">Both kinds</option>
                              {CALL_TYPES.map((t) => <option key={t} value={t}>{callTypeLabel[t]}</option>)}
                            </select>
                          )
                          : (asCallType(m.call_type) ? callTypeLabel[asCallType(m.call_type)!] : "Both kinds")}
                        {aiOn && m.ai_note && <div className="faint clip" title={m.ai_note}>{m.ai_note}</div>}
                        {aiOn && admin && (
                          <button className="link small" onClick={() => setNoteFor(m)}>
                            {m.ai_note ? "Edit note for the AI" : "Add a note for the AI"}
                          </button>
                        )}
                        {m.role === "caller" && (
                          // The in-call brief card on this person's phone (C17): per telecaller, on unless a manager
                          // switches it off.
                          <label className="brief-switch">
                            <input
                              type="checkbox"
                              checked={m.call_brief}
                              disabled={!isManagerRole(member.role) || busy}
                              onChange={(e) =>
                                void act(
                                  () => api.setCallBrief(m.id, e.target.checked),
                                  e.target.checked ? "Brief during calls on" : "Brief during calls off",
                                )}
                            />
                            Brief during calls
                          </label>
                        )}
                      </td>
                      <td>
                        <div>{fmtPhone(m.phone_e164)}</div>
                        <div className="faint">{m.email}</div>
                        {m.role === "caller" && (
                          <div className="faint">
                            {m.notice_accepted_at
                              ? `Notice accepted ${fmtDateTime(m.notice_accepted_at, tz)}`
                              : "Notice not accepted yet"}
                          </div>
                        )}
                      </td>
                      <td>
                        {device
                          ? (
                            <>
                              <div>
                                {[device.manufacturer, device.model].filter(
                                  Boolean,
                                ).join(" ") || "Phone"}
                              </div>
                              <div className="faint">
                                Seen {fmtDateTime(device.last_seen_at, tz)}
                              </div>
                              <SyncNote device={device} tz={tz} />
                              <PlanOnPhone device={device} plan={plan} />
                            </>
                          )
                          : (
                            <span className="faint">
                              {m.role === "caller" ? "Not set up yet" : "–"}
                            </span>
                          )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {canEdit && (
                          <ConfirmButton
                            label="Remove"
                            confirm="Their follow-ups pass to you and their phone is signed out."
                            disabled={busy}
                            onConfirm={() =>
                              void act(
                                () => api.removeMember(m.id),
                                `${m.display_name} removed`,
                              )}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {admin && <Invite onDone={reload} />}

        {admin && (
          <Card title="Invitations waiting" flush>
            {data.invitations.length === 0 && <Empty>No pending invitations.</Empty>}
            {data.invitations.length > 0 && (
              <div className="table-wrap">
                <table>
                  <tbody>
                    {data.invitations.map((i) => (
                      <tr key={i.id}>
                        <td className="primary">{i.display_name}</td>
                        <td>{roleLabel[i.role]}</td>
                        <td>{fmtPhone(i.phone_e164) || i.email}</td>
                        <td className="faint">
                          Expires {fmtDateTime(i.expires_at, tz)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <ConfirmButton
                            label="Cancel"
                            confirm="Cancel this invitation?"
                            disabled={busy}
                            onConfirm={() =>
                              void act(
                                () => api.revokeInvitation(i.id),
                                "Invitation cancelled",
                              )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        <Card title="Phones" flush>
          {data.devices.length === 0 && (
            <Empty>
              No phones registered yet.
            </Empty>
          )}
          {data.devices.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Phone</th>
                    <th>App</th>
                    <th>Plan on this phone</th>
                    <th>Last seen</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.devices.map((d) => (
                    <tr key={d.id}>
                      <td className="primary">
                        {names.get(d.member_id) ?? "–"}
                      </td>
                      <td>
                        {[d.manufacturer, d.model].filter(Boolean).join(" ") ||
                          "Phone"} <span className="faint">Android {d.os_version}</span>
                      </td>
                      <td>{d.app_version ?? "–"}</td>
                      <td>{d.revoked_at ? "–" : <PlanOnPhone device={d} plan={plan} />}</td>
                      <td>
                        <div className="nowrap">{fmtDateTime(d.last_seen_at, tz)}</div>
                        <SyncNote device={d} tz={tz} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {d.revoked_at ? <span className="badge danger">Revoked</span> : admin && (
                          <ConfirmButton
                            label="Revoke"
                            confirm="Sign this phone out now?"
                            disabled={busy}
                            onConfirm={() =>
                              void act(
                                () => api.revokeDevice(d.id),
                                "Phone revoked and signed out",
                              )}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {noteFor && (
          <NoteModal
            member={noteFor}
            onClose={() => setNoteFor(null)}
            onSaved={async () => {
              setNoteFor(null);
              await reload();
            }}
          />
        )}

        {removed.length > 0 && (
          <Card title="Removed">
            <p className="faint">
              Their calls and history stay. Invite them again to bring it back.
            </p>
            <div className="chips">
              {removed.map((m) => <span className="badge" key={m.id}>{m.display_name}</span>)}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}

function Invite({ onDone }: { onDone: () => Promise<void> }) {
  const { api, member } = useMember();
  const { busy, run } = useAction();
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("caller");
  const [contact, setContact] = useState("");
  const byEmail = contact.includes("@");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () =>
        api.invite({
          displayName: name.trim(),
          role,
          ...(byEmail ? { email: contact.trim() } : { phone: contact.trim() }),
        }),
      `${name.trim()} invited`,
    );
    if (ok) {
      setName("");
      setContact("");
      await onDone();
    }
  };

  return (
    <Card title="Invite someone">
      {member.under_review && (
        <p className="faint" style={{ marginTop: 0 }}>
          While your company is under review you can invite one phone number. Email invitations are not limited.
        </p>
      )}
      <form onSubmit={submit}>
        <div className="grid cols-3">
          <Field label="Name">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sneha Pillai"
            />
          </Field>
          <Field label="Role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {ROLES.filter((r) => member.role === "owner" || (r !== "owner" && r !== "admin")).map((r) => (
                <option key={r} value={r}>{roleLabel[r]}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Mobile number or email"
            hint="Telecallers sign in on the app with their mobile number."
          >
            <input
              required
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="98765 43210"
            />
          </Field>
        </div>
        <button
          className="primary"
          disabled={busy || !name.trim() || !contact.trim()}
        >
          Send invitation
        </button>
        <p className="faint" style={{ marginTop: 8 }}>
          The invitation lasts 7 days. They install the Voxowa app and sign in with this {byEmail ? "email" : "number"}.
        </p>
      </form>
    </Card>
  );
}

/** The note the AI reads with every call this person makes (TEAM-10). */
function NoteModal(props: { member: Member; onClose: () => void; onSaved: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [note, setNote] = useState(props.member.ai_note);
  const [workName, setWorkName] = useState(props.member.work_name ?? "");
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const patch = { ai_note: note.trim(), work_name: workName.trim() || null };
    if (await run(() => api.setCallProfile(props.member.id, patch), "Saved")) {
      await props.onSaved();
    }
  };
  return (
    <Modal title={`About ${props.member.display_name}'s calls`} onClose={props.onClose}>
      <form onSubmit={save}>
        <Field
          label="Work name"
          hint="The name they give on calls, if it is not their own. WhatsApp messages sign off with it, and the AI never takes it for the customer."
        >
          <input
            maxLength={60}
            value={workName}
            placeholder="Neha Singh"
            onChange={(e) => setWorkName(e.target.value)}
          />
        </Field>
        <Field
          label="Note for the AI"
          hint={`What their calls are about, for example the project or the kind of leads. Everyone in the team can read it. ${note.length} / ${MAX_NOTE}`}
        >
          <textarea
            rows={4}
            maxLength={MAX_NOTE}
            value={note}
            placeholder="Calls Facebook leads for Green Meadows, mostly in Kannada and English."
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <div className="row">
          <button className="primary" disabled={busy}>Save</button>
          <button type="button" onClick={props.onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Whether the company's plan works on this phone (APP-26): each feature green where the phone allows what it needs,
 * red where it does not, with what is missing; what would help in amber.
 */
function PlanOnPhone({ device, plan }: { device: Device; plan: { coach: boolean; autopilot: boolean } | undefined }) {
  const r = phoneReadiness(plan, device.permissions);
  if (!r.reported) return <div className="faint">Permissions not reported yet (needs the new app)</div>;
  return (
    <div className="plan-on-phone">
      <div className="plan-features">
        {r.features.map((f) => (
          <span
            key={f.name}
            className={`badge ${f.works ? "ok" : "danger"}`}
            title={f.works ? "Works on this phone" : "Needs a permission"}
          >
            {f.works ? "✓" : "!"} {f.name}
          </span>
        ))}
      </div>
      {r.problems.map((p) => <div key={p} className="sync-problem">{p}</div>)}
      {r.advice.map((a) => <div key={a} className="plan-advice">{a}</div>)}
    </div>
  );
}

/** How the phone's last sync went (DEV-03): a problem stands out, so a phone that stopped sending recordings is seen. */
function SyncNote({ device, tz }: { device: Device; tz: string }) {
  const line = syncLine(device, tz);
  if (!line) return null;
  return <div className={line.problem ? "sync-problem" : "faint"}>{line.text}</div>;
}
